import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Agent {
  name: string;
  title: string;
  emoji: string;
  description: string;
  status: 'active' | 'idle';
  color: string;
  gradient: string;
  stats: {
    primary: string;
    secondary: string;
  };
}

const AgentCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1);

  // ACTIVE AGENTS ONLY - Real MacMind system agents
  const agents: Agent[] = [
    {
      name: 'Guardian',
      title: 'Security & Threat Monitor',
      emoji: '🛡️',
      description: 'Analyzes 227,000+ DNS queries daily via Pi-hole. Detects threats, blocks malicious domains, protects family network from cyber attacks.',
      status: 'active',
      color: 'from-red-500 to-orange-500',
      gradient: 'bg-gradient-to-br from-red-500/20 to-orange-500/20',
      stats: {
        primary: '147 blocked today',
        secondary: '99.8% uptime',
      },
    },
    {
      name: 'Caretaker',
      title: 'System Health Manager',
      emoji: '🔧',
      description: 'Monitors disk space, CPU load, memory usage, and service health. Proactively maintains MacMind infrastructure and alerts on issues.',
      status: 'active',
      color: 'from-green-500 to-emerald-500',
      gradient: 'bg-gradient-to-br from-green-500/20 to-emerald-500/20',
      stats: {
        primary: '100% health',
        secondary: '342 tasks completed',
      },
    },
    {
      name: 'Network Agent',
      title: 'Device Discovery & Mapping',
      emoji: '🌐',
      description: 'Scans network every minute, tracks 95 devices, maps topology, detects new devices, monitors connectivity across home network.',
      status: 'active',
      color: 'from-blue-500 to-cyan-500',
      gradient: 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20',
      stats: {
        primary: '81 devices active',
        secondary: '809 snapshots',
      },
    },
    {
      name: 'Herald',
      title: 'Notifications & Alerts',
      emoji: '📢',
      description: 'Delivers critical alerts via Discord. Sends security warnings, system health updates, and important family notifications in real-time.',
      status: 'active',
      color: 'from-purple-500 to-pink-500',
      gradient: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
      stats: {
        primary: '23 sent today',
        secondary: '<1 min response',
      },
    },
  ];

  // Auto-rotate carousel
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % agents.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, agents.length]);

  const next = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % agents.length);
  };

  const prev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + agents.length) % agents.length);
  };

  const getVisibleAgents = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + agents.length) % agents.length;
      visible.push({ agent: agents[index], position: i, index });
    }
    return visible;
  };

  return (
    <div className="relative w-full py-20 px-4">
      {/* Section Title */}
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
          Active Agent Team
        </h2>
        <p className="text-xl text-gray-400">
          Four autonomous agents protecting your network 24/7
        </p>
      </div>

      {/* 3D Carousel Container */}
      <div
        className="relative h-[500px] md:h-[600px] perspective-3d"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative h-full flex items-center justify-center">
          <AnimatePresence mode="popLayout">
            {getVisibleAgents().map(({ agent, position, index }) => (
              <motion.div
                key={index}
                initial={{
                  x: position * 400,
                  scale: position === 0 ? 1 : 0.7,
                  opacity: 0,
                  rotateY: position * 20,
                  z: position === 0 ? 0 : -200,
                }}
                animate={{
                  x: position * 400,
                  scale: position === 0 ? 1 : 0.7,
                  opacity: position === 0 ? 1 : 0.4,
                  rotateY: position * 20,
                  z: position === 0 ? 0 : -200,
                }}
                exit={{
                  x: direction * -400,
                  scale: 0.5,
                  opacity: 0,
                }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                className={`absolute ${position === 0 ? 'z-20' : 'z-10'}`}
                style={{
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Agent Card */}
                <div
                  className={`glass ${agent.gradient} rounded-2xl p-8 w-[350px] md:w-[400px] border-2 border-white/20 shadow-2xl`}
                >
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          agent.status === 'active' ? 'bg-green-400 animate-pulse' : 'bg-gray-400'
                        }`}
                      />
                      <span className="text-sm font-medium text-gray-300 uppercase tracking-wider">
                        {agent.status}
                      </span>
                    </div>
                  </div>

                  {/* Agent Icon */}
                  <div className="flex justify-center mb-6">
                    <div className={`text-7xl bg-gradient-to-br ${agent.color} bg-clip-text text-transparent`}>
                      {agent.emoji}
                    </div>
                  </div>

                  {/* Agent Info */}
                  <h3 className="text-2xl font-bold text-white mb-2 text-center">
                    {agent.name}
                  </h3>
                  <div className={`text-sm font-semibold text-transparent bg-gradient-to-r ${agent.color} bg-clip-text mb-4 text-center uppercase tracking-wide`}>
                    {agent.title}
                  </div>
                  <p className="text-gray-300 text-center leading-relaxed mb-6">
                    {agent.description}
                  </p>

                  {/* Stats */}
                  <div className="flex justify-center gap-4 text-center">
                    <div className="flex-1">
                      <div className="text-xs text-gray-400 mb-1">Threats</div>
                      <div className={`text-sm font-bold text-transparent bg-gradient-to-r ${agent.color} bg-clip-text`}>
                        {agent.stats.primary}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-400 mb-1">Uptime</div>
                      <div className={`text-sm font-bold text-transparent bg-gradient-to-r ${agent.color} bg-clip-text`}>
                        {agent.stats.secondary}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prev}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 rounded-full glass glass-hover flex items-center justify-center text-white hover:text-cyan-400 transition-colors"
        >
          ←
        </button>
        <button
          onClick={next}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 rounded-full glass glass-hover flex items-center justify-center text-white hover:text-cyan-400 transition-colors"
        >
          →
        </button>
      </div>

      {/* Indicators */}
      <div className="flex justify-center gap-2 mt-8">
        {agents.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-cyan-400 w-8' : 'bg-gray-600 hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default AgentCarousel;
